// Operator sidebar (design spec 2026-10-19 §4): the console pages, then one row per company the
// agency is granted (from `/v1/me`, no extra call), keeping the console's week.
import { CompanyTile, NavList, NavRow, SidebarCaption, SidebarPanel } from "@companyos/ui";
import { Link } from "@tanstack/react-router";
import {
  BotIcon,
  Building2Icon,
  ClipboardListIcon,
  CpuIcon,
  CreditCardIcon,
  FlagIcon,
  HeartPulseIcon,
  LayersIcon,
  LightbulbIcon,
  PlugIcon,
  ReceiptIcon,
  RocketIcon,
  ServerIcon,
  ShieldAlertIcon,
  ShieldIcon,
  SlidersHorizontalIcon,
  TimerIcon,
  TriangleAlertIcon,
  UsersIcon,
  WrenchIcon,
} from "lucide-react";
import type { MeResponse } from "../../api/types";
import { keepFocus } from "../../focus/focus";

export function OperatorSidebar({ me }: { me: MeResponse }) {
  const grants = [...(me.agency?.grants ?? [])].sort((a, b) =>
    a.company_name.localeCompare(b.company_name),
  );
  return (
    <SidebarPanel title="Operator console" aria-label="Operator">
      <NavList>
        <NavRow
          icon={Building2Icon}
          label="Companies"
          render={<Link to="/operator" activeOptions={{ exact: true, includeSearch: false }} />}
        />
        {/* SUPER-ADMIN: the workspace's clients (super admin: all; operator: assigned). */}
        <NavRow
          icon={UsersIcon}
          label="Clients"
          render={<Link to="/operator/clients" activeOptions={{ includeSearch: false }} />}
        />
        {/* ONBOARDING-P3: the clients in onboarding. */}
        <NavRow
          icon={ClipboardListIcon}
          label="Onboarding"
          render={<Link to="/operator/onboarding" activeOptions={{ includeSearch: false }} />}
        />
        {/* SKILLSMITH-CONTROL: each client's Skillsmith (super admins; the API gates it). */}
        <NavRow
          icon={WrenchIcon}
          label="Skillsmith"
          render={<Link to="/operator/skillsmith" activeOptions={{ includeSearch: false }} />}
        />
        <NavRow
          icon={FlagIcon}
          label="Gate tracker"
          render={<Link to="/operator/gate" activeOptions={{ includeSearch: false }} />}
        />
        <NavRow
          icon={BotIcon}
          label="Digital employees"
          render={<Link to="/operator/des" activeOptions={{ includeSearch: false }} />}
        />
        <NavRow
          icon={CpuIcon}
          label="System agents"
          render={<Link to="/operator/system-agents" activeOptions={{ includeSearch: false }} />}
        />
        <NavRow
          icon={HeartPulseIcon}
          label="Agent health"
          render={<Link to="/operator/agent-health" activeOptions={{ includeSearch: false }} />}
        />
        {/* SKILL-PROPOSALS-OPERATOR: the operator-only skill-proposal queue (super admins). */}
        <NavRow
          icon={LightbulbIcon}
          label="Skill proposals"
          render={<Link to="/operator/skill-proposals" activeOptions={{ includeSearch: false }} />}
        />
        <NavRow
          icon={LayersIcon}
          label="Templates"
          render={<Link to="/operator/templates" activeOptions={{ includeSearch: false }} />}
        />
        <NavRow
          icon={RocketIcon}
          label="Deploys"
          render={<Link to="/operator/deploys" activeOptions={{ includeSearch: false }} />}
        />
        <NavRow
          icon={TriangleAlertIcon}
          label="Failed events"
          render={<Link to="/operator/consumers" activeOptions={{ includeSearch: false }} />}
        />
      </NavList>
      {/* M5-05: the agency console. */}
      <SidebarCaption label="Agency" />
      <NavList>
        <NavRow
          icon={TimerIcon}
          label="Approvals SLA"
          render={<Link to="/operator/approvals" activeOptions={{ includeSearch: false }} />}
        />
        <NavRow
          icon={UsersIcon}
          label="Supervisor load"
          render={<Link to="/operator/supervisors" activeOptions={{ includeSearch: false }} />}
        />
        <NavRow
          icon={ReceiptIcon}
          label="Cost to serve"
          render={<Link to="/operator/cost" activeOptions={{ includeSearch: false }} />}
        />
        <NavRow
          icon={CreditCardIcon}
          label="Billing"
          render={<Link to="/operator/billing" activeOptions={{ includeSearch: false }} />}
        />
        <NavRow
          icon={ShieldAlertIcon}
          label="Break-glass"
          render={<Link to="/operator/break-glass" activeOptions={{ includeSearch: false }} />}
        />
      </NavList>
      {/* CONNECTIONS: the platform's service endpoints (agency admins; the API decides who). */}
      {me.agency?.role === "admin" ? (
        <>
          <SidebarCaption label="Platform" />
          <NavList>
            <NavRow
              icon={PlugIcon}
              label="Connections"
              render={<Link to="/operator/connections" activeOptions={{ includeSearch: false }} />}
            />
            <NavRow
              icon={ServerIcon}
              label="Satellites"
              render={<Link to="/operator/satellites" activeOptions={{ includeSearch: false }} />}
            />
            {/* MONITORING-P1: the guard rules over DE telemetry. */}
            <NavRow
              icon={ShieldIcon}
              label="Guards"
              render={<Link to="/operator/guards" activeOptions={{ includeSearch: false }} />}
            />
            {/* OPERATOR-CONSOLE: the versioned settings and the System operator agents. */}
            <NavRow
              icon={SlidersHorizontalIcon}
              label="Settings"
              render={<Link to="/operator/settings" activeOptions={{ includeSearch: false }} />}
            />
            <NavRow
              icon={CpuIcon}
              label="Operator agents"
              render={
                <Link
                  to="/operator/settings"
                  search={{ section: "agents" }}
                  activeOptions={{ includeSearch: true }}
                />
              }
            />
          </NavList>
        </>
      ) : null}
      {grants.length > 0 ? (
        <>
          <SidebarCaption label="Companies" />
          <NavList>
            {grants.map((g) => (
              <NavRow
                key={g.company_id}
                label={g.company_name}
                lead={<CompanyTile name={g.company_name} size={20} />}
                render={<Link to="/c/$slug" params={{ slug: g.company_slug }} search={keepFocus} />}
              />
            ))}
          </NavList>
        </>
      ) : null}
    </SidebarPanel>
  );
}
